import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import pick from 'lodash/pick';

import {
  MARKETING_MATERIAL_PRIMARY_TEXT_DISPLAY_ENUM,
  MARKETING_MATERIAL_SECONDARY_TEXT_DISPLAY_ENUM,
} from '../../../../views/AlbumFolder/AlbumFolderMarketing/constants';
import AccountMarketingMaterialEventFlyerTemplate from '../../../../views/AlbumFolder/AlbumFolderMarketing/components/AccountMarketingMaterialPrintTemplate/AccountMarketingMaterialEventFlyerTemplate';
import Button from '../../../../../../components/global/Button';
import Angle from '../../../../../../components/global/Angle';
import {
  MARKETING_MATERIAL_EVENT_FLYER_CONFIGURATION_ACTION,
  MARKETING_MATERIAL_EVENT_FLYER_CONFIGURATION_FIELD,
} from '../../../../views/AlbumFolder/AlbumFolderMarketing/components/AccountMarketingMaterialPrintTemplate/AccountMarketingMaterialEventFlyerTemplate/AccountMarketingMaterialEventFlyerTemplate';
import TextEditorModal from './components/TextEditorModal';
import useFeaturePermission from '../../../../services/featurePermissions';
import { PURCHASED_EVENT_ADDON_FEATURES } from '../../../../services/featurePermissions/helpers';
import { isFormDirty } from '../../../../../../util/helpers';
import SaveChangesRequestConfirm, {
  MARKETING_MATERIAL_CONFIGURATOR_SAVE_CHANGES_MODAL,
} from './components/SaveChangesRequestConfirm';
import useOpenModal from '../../../../../../hooks/useOpenModal';

const propTypes = {
  label: PropTypes.string,
  marketingMaterialType: PropTypes.string,
  albumFolder: PropTypes.shape({
    id: PropTypes.string,
    album_type: PropTypes.string,
    album_subtype: PropTypes.string,
  }),
  onClose: PropTypes.func,
  onConfigurationComplete: PropTypes.func.isRequired,
  onUpsellRequired: PropTypes.func.isRequired,
};

const MarketingMaterialConfigurator = ({
  label,
  onClose,
  albumFolder,
  defaultConfiguration,
  marketingMaterialType,
  onConfigurationComplete,
  onUpsellRequired,
}) => {
  const [editing, setEditing] = useState(false);
  const openModal = useOpenModal();
  const featurePermissionItem = useFeaturePermission({
    albumFolderId: albumFolder?.id,
    features: [
      PURCHASED_EVENT_ADDON_FEATURES.white_label_marketing_prints_enabled,
    ],
  });

  const onFormSubmit = values => {
    onConfigurationComplete({
      configuration: values,
      marketingMaterialType,
    });
  };

  const baseForm = {
    primary_text_display:
      MARKETING_MATERIAL_PRIMARY_TEXT_DISPLAY_ENUM.EVENT_NAME,
    secondary_text_display:
      MARKETING_MATERIAL_SECONDARY_TEXT_DISPLAY_ENUM.EVENT_DATE_AND_LOCATION,
    secondary_text_personalized_message: '',
    display_watermark: false,
    ...pick(defaultConfiguration || {}, [
      'primary_text_display',
      'secondary_text_display',
      'secondary_text_personalized_message',
      'display_watermark',
    ]),
  };

  return (
    <>
      <Formik
        initialValues={baseForm}
        onSubmit={onFormSubmit}
        render={({ values, handleChange, handleSubmit }) => {
          const isDirty = isFormDirty(baseForm, values);

          const onFieldUpdate = ({ field, action }) => {
            switch (field) {
              case MARKETING_MATERIAL_EVENT_FLYER_CONFIGURATION_FIELD.PRIMARY_TEXT_FIELD: {
                if (
                  action ===
                  MARKETING_MATERIAL_EVENT_FLYER_CONFIGURATION_ACTION.DELETE
                ) {
                  handleChange({
                    target: {
                      name: 'primary_text_display',
                      value: MARKETING_MATERIAL_PRIMARY_TEXT_DISPLAY_ENUM.NONE,
                    },
                  });
                }

                break;
              }
              case MARKETING_MATERIAL_EVENT_FLYER_CONFIGURATION_FIELD.SECONDARY_TEXT_FIELD: {
                if (
                  action ===
                  MARKETING_MATERIAL_EVENT_FLYER_CONFIGURATION_ACTION.DELETE
                ) {
                  handleChange({
                    target: {
                      name: 'secondary_text_display',
                      value:
                        MARKETING_MATERIAL_SECONDARY_TEXT_DISPLAY_ENUM.NONE,
                    },
                  });

                  handleChange({
                    target: {
                      name: 'secondary_text_personalized_message',
                      value: '',
                    },
                  });
                }

                if (
                  action ===
                  MARKETING_MATERIAL_EVENT_FLYER_CONFIGURATION_ACTION.EDIT
                ) {
                  if (
                    !featurePermissionItem.white_label_marketing_prints_enabled
                      .enabled
                  ) {
                    onUpsellRequired();
                    return;
                  }

                  setEditing(true);
                }

                break;
              }
              default:
                break;
            }
          };

          return (
            <>
              {onClose && (
                <Button
                  btnStyle="link"
                  className="mr-2"
                  onClick={() => {
                    if (!isDirty) {
                      onClose();
                      return;
                    }

                    openModal(
                      MARKETING_MATERIAL_CONFIGURATOR_SAVE_CHANGES_MODAL,
                    );
                  }}
                >
                  <Angle direction="left" />{' '}
                  <span className="d-none d-md-inline-block ml-1">Back</span>
                </Button>
              )}

              <h3 className="text-center">Personalize your {label}</h3>

              <div className="marketing-material-live-preview-container">
                <AccountMarketingMaterialEventFlyerTemplate
                  albumFolder={albumFolder}
                  configuration={values}
                  onFieldUpdate={onFieldUpdate}
                  configurationModeEnabled
                />
              </div>
              <div className="marketing-material-configurator-actions">
                <p className="text-sm text-faded text-center my-2">
                  Tap the ‘Edit’ button to personalize text fields.
                </p>

                <div className="configurator-button-group">
                  {values.primary_text_display ===
                    MARKETING_MATERIAL_PRIMARY_TEXT_DISPLAY_ENUM.NONE && (
                    <Button
                      onClick={() => {
                        handleChange({
                          target: {
                            name: 'primary_text_display',
                            value:
                              MARKETING_MATERIAL_PRIMARY_TEXT_DISPLAY_ENUM.EVENT_NAME,
                          },
                        });
                      }}
                      btnOutline
                      btnSize="sm"
                    >
                      Add upper text field
                    </Button>
                  )}
                  {values.secondary_text_display ===
                    MARKETING_MATERIAL_SECONDARY_TEXT_DISPLAY_ENUM.NONE && (
                    <Button
                      onClick={() => {
                        handleChange({
                          target: {
                            name: 'secondary_text_display',
                            value:
                              MARKETING_MATERIAL_SECONDARY_TEXT_DISPLAY_ENUM.EVENT_DATE_AND_LOCATION,
                          },
                        });
                      }}
                      btnOutline
                      btnSize="sm"
                    >
                      Add lower text field
                    </Button>
                  )}
                </div>
                <Button
                  onClick={handleSubmit}
                  btnSize="sm"
                  className="mt-1 w-100"
                >
                  Save design
                </Button>
              </div>
              <SaveChangesRequestConfirm
                onSave={handleSubmit}
                onLeave={onClose}
              />
              <TextEditorModal
                forceOpen={editing}
                maxLength={50}
                defaultValue={values.secondary_text_personalized_message}
                onClose={() => setEditing(false)}
                onChange={value => {
                  handleChange({
                    target: {
                      name: 'secondary_text_display',
                      value:
                        MARKETING_MATERIAL_SECONDARY_TEXT_DISPLAY_ENUM.PERSONALIZED_MESSAGE,
                    },
                  });
                  handleChange({
                    target: {
                      name: 'secondary_text_personalized_message',
                      value,
                    },
                  });
                }}
              />
            </>
          );
        }}
      />
    </>
  );
};

MarketingMaterialConfigurator.propTypes = propTypes;

export default MarketingMaterialConfigurator;
